import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin } from 'rxjs';
import { LPGDistService, ToolbarItem } from '../../lpg-dist.service';
import moment from 'moment-jalaali';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { DateRange } from 'src/app/shared/components/date-range.component';
import { LpgDistToolbarComponent } from 'src/app/lpg-dist/lpg-dist-toolbar/lpg-dist-toolbar.component';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';

import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum, PgTitleComponent,
  PgToolbarComponent
} from '../../../shared/components/title-bar/title-bar.component';
import saveAs from 'file-saver';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { SharedModule } from 'src/app/shared/shared.module';

interface DailyReportComponentSettings {
  companyId: string;
  calendar: boolean;
}

@Component({
  selector: 'app-daily-report',
  templateUrl: './daily-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports:[SharedModule],
})
export class DailyReportComponent implements OnInit, PgTitleComponent, PgDateRangeTitleComponent, PgToolbarComponent {
  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();
  toolbarComponentType = LpgDistToolbarComponent;
  toolbarComponent!: LpgDistToolbarComponent;
  reports: any[] = [];
  organisations: Organisation[] = [];
  dateRange: DateRange = {
    startDate: new Date(),
    endDate: new Date()
  };
  _settings!: DailyReportComponentSettings;

  constructor(
    private _lpgDistService: LPGDistService,
    private _organisationService: OrganisationService,
    private _userLocalStorage: UserLocalStoreService,
    private _pgMessageService: PgMessagesService) { }

  ngOnInit() {
    this._settings = this._userLocalStorage.get<DailyReportComponentSettings>('DailyReportComponent');
    if (!this._settings) {
      this._settings = { companyId: 'iran.parsagroup', calendar: true };
    }
    const endDate = moment().subtract(1, 'days').endOf('day').toDate();
    this.dateRange = {
      startDate: moment(endDate).startOf('jMonth').toDate(),
      endDate,
    };
    this.toolbarComponent.itemsSelected.subscribe(t => {
      this.refresh(t);
    });
    this.toolbarComponent.excelExport = this.xlsxReport;

    this.fillDataTable();
  }

  setToolbar() {
    this.toolbarComponent.dateRange = {
      startDate: this.dateRange.endDate,
      endDate: this.dateRange.endDate
    };
    this.toolbarComponent.visibleOrganisation = true;
    this.toolbarComponent.organisations = this.organisations
      .filter(o => ['domestic'].filter(s => o.properties[s]).length > 0);
    this.toolbarComponent.selectedOrganisation = this.organisations
      .find(o => o.name === this._settings.companyId) || Organisation.create();
    this.toolbarComponent.jalali = this._settings.calendar;
    this.toolbarComponent.singleDate = true;
    this.toolbarComponent.visibleButton = true;
  }

  fillDataTable() {
    this.title.emit('Daily report');
    this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.persian]);
    forkJoin([
      this._lpgDistService.getDomesticOrganisationReport(
        this._settings.companyId,
        this.dateRange.startDate.getTime(),
        this.dateRange.endDate.getTime(), moment(this.dateRange.endDate).endOf('jMonth').toDate().getTime()),
      this._organisationService.get(),
    ]).subscribe(result => {
      this.organisations = result[1];
      this.setToolbar();
      this.reports = result[0].filter(d => d.organisation !== 'iran.parsagroup');
    });
  }

  organisationFormatter(data: string) {
    return this.organisations.find(o => o.name === data)?.nameTranslations['fa'];
  }

  getSumOfLoadings(organisationLoading: any[]) {
    return {
      sumOfLoading: Math.round(organisationLoading.reduce((p, v) => p + v.total, 0)),
      sumOfDailyLoading: Math.round(organisationLoading.reduce((p, v) => p + v.selectedDayLoading, 0)),
      sumOfEstimate: Math.round(organisationLoading.reduce((p, v) => p + v.estimate, 0)),
      sumOfQuota: Math.round(organisationLoading.reduce((p, v) => p + v.quota, 0)),
      sumOfQuotaToDate: Math.round(organisationLoading.reduce((p, v) => p + v.quotaToDate, 0)),
      sumOfbalance: Math.round(organisationLoading.reduce((p, v) => p + v.balance, 0)),
    };
  }

  getSumOfDischarge(organisationLoading: any[]) {
    return {
      sumOfLoading: Math.round(organisationLoading.reduce((p, v) => p + v.total, 0)),
      sumOfDailyLoading: Math.round(organisationLoading.reduce((p, v) => p + v.selectedDayDischarge, 0)),
      sumOfEstimate: Math.round(organisationLoading.reduce((p, v) => p + v.estimate, 0)),
      sumOfQuota: Math.round(organisationLoading.reduce((p, v) => p + v.quota, 0)),
      sumOfQuotaToDate: Math.round(organisationLoading.reduce((p, v) => p + v.quotaToDate, 0)),
      sumOfLoadingbalance: Math.round(organisationLoading.reduce((p, v) => p + v.balance, 0)),
      sumOfSold: Math.round(organisationLoading.reduce((p, v) => p + v.sold, 0)),
      sumOfSolds: Math.round(organisationLoading.reduce((p, v) => p + v.sumOfSold, 0)),
      sumOfStored: Math.round(organisationLoading.reduce((p, v) => p + v.stored, 0)),
    };
  }

  getSumOfSolds() {
    return this.reports.flatMap(g => g.discharges).reduce((p, v) => p + v.sumOfSold, 0);
  }

  getSumOfAllLoadings() {
    const data = this.reports.map(l => this.getSumOfLoadings(l.loadings));
    return {
      sumOfLoading: Math.round(data.reduce((p, v) => p + v.sumOfLoading, 0)),
      sumOfDailyLoading: Math.round(data.reduce((p, v) => p + v.sumOfDailyLoading, 0)),
      sumOfEstimate: Math.round(data.reduce((p, v) => p + v.sumOfEstimate, 0)),
      sumOfbalance: Math.round(data.reduce((p, v) => p + v.sumOfbalance, 0)),
      average: Math.round(data.reduce((p, v) => p + v.sumOfLoading, 0) / (moment(this.dateRange.endDate)
        .diff(moment(this.dateRange.startDate), 'days') + 1)),
      payments: this.reports.reduce((p, v) => p + v.paymentBalances, 0)
    };
  }

  xlsxReport(lpgDistService: LPGDistService, dateRange: DateRange, organisationId?: string) {
    lpgDistService.downLoadDailyReportExcel(
      organisationId || '',
      moment(this.dateRange.endDate).startOf('jMonth').toDate().getTime(),
      dateRange.endDate.getTime(),
      moment(this.dateRange.endDate).endOf('jMonth').toDate().getTime()
    ).subscribe(b => {
      saveAs(b.body || '', `${DateTimeHelpers.formatPersianLocalDate(this.dateRange.startDate.getTime())}-to-` +
        `${DateTimeHelpers.formatPersianLocalDate(this.dateRange.endDate.getTime())}-lpg-dist-daily-report.xlsx`);
    }, () => {
      this._pgMessageService.publishMessage({
        level: MessageLevel.Error,
        message: 'Could not download excel report',
        topic: 'Daily Report'
      });
    });
  }

  refresh(item: ToolbarItem) {
    this._settings = {
      companyId: item.companyId || '',
      calendar: item.selectedCalander || false
    };
    this._userLocalStorage.save('DailyReportComponent', this._settings);
    this.dateRange = {
      startDate: moment(item.dateRange.endDate).startOf('jMonth').toDate(),
      endDate: item.dateRange.endDate,
    };
    this.fillDataTable();
  }
}
